/**
 * Single source of truth for dashboard container keys.
 *
 * ⚠️ These string values are a persisted cross-system contract.
 * A container's key is sent to the backend and stored as is in:
 *   - `WorkQueueSnooze.container_key` (snooze records), and
 *   - the per-user enabled/disabled container preferences.
 *
 * Therefore the values below only be appended and not renamed.
 * Renaming a value silently orphans every existing snooze (and toggle-state)
 * row keyed by the old string.
 * If a key truly must change, ship a backend data migration that rewrites
 * WorkQueueSnooze.container_key.
 */

export const HH_CONTAINER_KEYS = {
    incompleteCareTeam: 'hhIncompleteCareTeam',
    soc: 'hhSoc',
    roc: 'hhRoc',
    eval: 'hhEval',
    expiringFulfillmentWindow: 'hhExpiringFulfillmentWindow',
    patientsNeedingScheduling: 'hhPatientsNeedingScheduling',
    patientNoShows: 'hhPatientNoShows',
    pendingEndOfEpisode: 'hhPendingEndOfEpisode',
    transfers: 'hhTransfers',
    dischargeWithPendingVisits: 'hhDischargeWithPendingVisits',
    inactiveClinicianVisits: 'hhInactiveClinicianVisits',
    noFutureAppointments: 'hhNoFutureAppointments',
    completedVisitsNotMappedToOrders: 'hhCompletedVisitsNotMappedToOrders',
    evalsBeforeSocs: 'hhEvalsBeforeSocs',
    multipleOasisPerEpisode: 'hhMultipleOasisPerEpisode',
    pendingReassessment: 'hhPendingReassessment',
    overdueVisits: 'hhOverdueVisits',
    overscheduledPatients: 'hhOverscheduledPatients',
    underscheduledPatients: 'hhUnderscheduledPatients',
    discontinuedFrequenciesWithPendingVisits: 'hhDiscontinuedFrequenciesWithPendingVisits',
    disciplineDischargePatientsWithPendingVisits: 'hhDisciplineDischargePatientsWithPendingVisits',
    upcomingDischargeWithFuturePendingVisits: 'hhUpcomingDischargeWithFuturePendingVisits',
    allTransferredPatients: 'hhAllTransferredPatients',
    missedVisits: 'hhMissedVisits',
    visitsWithoutActiveShifts: 'hhVisitsWithoutActiveShifts',
    pendingHhaSupervisory: 'hhPendingHhaSupervisory',
    pendingLpnSupervisory: 'hhPendingLpnSupervisory',
    pendingTherapySupervisory: 'hhPendingTherapySupervisory',
} as const;

export const NORTH_STAR_CONTAINER_KEYS = {
    rnAdmitVisits: 'northStarRnAdmitVisits',
    recentAdmits: 'northStarRecentAdmits',
    newFrequencies: 'northStarNewssddsFrequencies',
    patientsNeedingScheduling: 'northStarPatientsNeedingScheduling',
    expiringFulfillmentWindowVisits: 'northStarExpiringFulfillmentWindowVisits',
    incompleteCareTeam: 'northStarIncompleteCareTeam',
    unassignedVisits: 'northStarUnassignedVisits',
    deathDischargeWithVisits: 'northStarDeathDischargeWithVisits',
    inactiveClinicianVisits: 'northStarInactiveClinicianVisits',
    rn14DayVisit: 'northStarRn14DayVisit',
    recert: 'northStarRecert',
    huv: 'northStarHuv',
    sfv: 'northStarSfv',
    patientNoShows: 'northStarPatientNoShows',
    visitsWithoutActiveShifts: 'northStarVisitsWithoutActiveShifts',
    incompleteClinicianDetails: 'northStarIncompleteClinicianDetails',
} as const;

export const PINNACLE_CONTAINER_KEYS = {
    soc: 'pinnacleSoc',
    roc: 'pinnacleRoc',
    eval: 'pinnacleEval',
    completedVisitsMissingAnOrder: 'pinnacleCompletedVisitsMissingAnOrder',
    visitsScheduledBeforeSoc: 'pinnacleVisitsScheduledBeforeSoc',
    urgentVisitsNeedingScheduling: 'pinnacleUrgentVisitsNeedingScheduling',
    upcomingEndOfEpisode: 'pinnacleUpcomingEndOfEpisode',
    patientsNeedingScheduling: 'pinnaclePatientsNeedingScheduling',
    inactiveClinicianVisits: 'pinnacleInactiveClinicianVisits',
    discontinuedFrequenciesWithPendingVisits: 'pinnacleDiscontinuedFrequenciesWithPendingVisits',
    transferredPatientsWithPendingVisits: 'pinnacleTransferredPatientsWithPendingVisits',
    allTransferredPatients: 'pinnacleAllTransferredPatients',
    upcomingDischargeWithFuturePendingVisits: 'pinnacleUpcomingDischargeWithFuturePendingVisits',
    conflictingDischargeAndRecertVisits: 'pinnacleConflictingDischargeAndRecertVisits',
    overscheduledPatients: 'pinnacleOverscheduledPatients',
    underscheduledPatients: 'pinnacleUnderscheduledPatients',
    patientsMissingCms485: 'pinnaclePatientsMissingCms485',
    patientsMissingNomnc: 'pinnaclePatientsMissingNomnc',
    patientsMissingScicOrder: 'pinnaclePatientsMissingScicOrder',
    patientsMissingPostHospitalOrder: 'pinnaclePatientsMissingPostHospitalOrder',
    pendingTherapyReevaluation: 'pinnaclePendingTherapyReevaluation',
    pendingTherapySupervisory: 'pinnaclePendingTherapySupervisory',
    pendingHhaSupervisory: 'pinnaclePendingHhaSupervisory',
    activePatientsWithNoVisits: 'pinnacleActivePatientsWithNoVisits',
    pendingLpnSupervisory: 'pinnaclePendingLpnSupervisory',
    unresolvedEpisodes: 'pinnacleUnresolvedEpisodes',
    incompleteCareTeam: 'pinnacleIncompleteCareTeam',
    potentialReschedules: 'pinnaclePotentialReschedules',
    agencyDischargedPatientsWithPendingVisits: 'pinnacleAgencyDischargedPatientsWithPendingVisits',
    visitsAssignedToContractAgency: 'pinnacleVisitsAssignedToContractAgency',
    disciplineDischargePatientsWithPendingVisits:
        'pinnacleDisciplineDischargePatientsWithPendingVisits',
    missedVisits: 'pinnacleMissedVisits',
    visitsWithoutActiveShifts: 'pinnacleVisitsWithoutActiveShifts',
    visitsAwaitingAuthorization: 'pinnacleVisitsAwaitingAuthorization',
} as const;

export const ELEANOR_CONTAINER_KEYS = {
    rnPositionNeeded: 'eleanorRnPositionNeeded',
    rnPatientPayerMismatch: 'eleanorRnPatientPayerMismatch',
    unassigned: 'eleanorUnassigned',
    visitClinicianAppointmentSyncIssues: 'eleanorVisitClinicianAppointmentSyncIssues',
    unsyncedPatients: 'eleanorUnsyncedPatients',
} as const;

export const COFK_CONTAINER_KEYS = {
    eval: 'cofkEval',
    patientsNeedingScheduling: 'cofkPatientsNeedingScheduling',
    disciplineDischargePatientsWithPendingVisits:
        'cofkDisciplineDischargePatientsWithPendingVisits',
    visitSyncIssues: 'cofkVisitSyncIssues',
    visitsMismatchingPatientPreference: 'cofkVisitsMismatchingPatientPreference',
} as const;

export const HOSPICE_DEMO_CONTAINER_KEYS = {
    evalVisitRequests: 'demoHospiceEvalVisitRequests',
    admissionsVisits: 'demoHospiceAdmissionsVisits',
    incompleteCareTeam: 'demoHospiceIncompleteCareTeam',
    patientsNeedingScheduling: 'demoHospicePatientsNeedingScheduling',
    expiringFulfillmentWindow: 'demoHospiceExpiringFulfillmentWindow',
    patientNoShows: 'demoHospicePatientNoShows',
    recert: 'demoHospiceRecert',
    huv: 'demoHospiceHuv',
    sfv: 'demoHospiceSfv',
    unassigned: 'demoHospiceUnassigned',
    recentAdmits: 'demoHospiceRecentAdmits',
    newFrequencies: 'demoHospiceNewFrequencies',
    deathDischargeWithVisits: 'demoHospiceDeathDischargeWithVisits',
    inactiveClinicianVisits: 'demoHospiceInactiveClinicianVisits',
    visitsWithoutActiveShifts: 'demoHospiceVisitsWithoutActiveShifts',
} as const;

export const TELADOC_CONTAINER_KEYS = {
    unassigned: 'teladocUnassigned',
    shiftBlockSyncIssues: 'teladocShiftBlockSyncIssues',
} as const;

export const PENN_CONTAINER_KEYS = {
    incompleteCareTeam: 'pennIncompleteCareTeam',
    soc: 'pennSoc',
    roc: 'pennRoc',
    eval: 'pennEval',
    completedVisitsMissingAnOrder: 'pennCompletedVisitsMissingAnOrder',
    visitsScheduledBeforeSoc: 'pennVisitsScheduledBeforeSoc',
    urgentVisitsNeedingScheduling: 'pennUrgentVisitsNeedingScheduling',
    upcomingEndOfEpisode: 'pennUpcomingEndOfEpisode',
    patientsNeedingScheduling: 'pennPatientsNeedingScheduling',
    inactiveClinicianVisits: 'pennInactiveClinicianVisits',
    discontinuedFrequenciesWithPendingVisits: 'pennDiscontinuedFrequenciesWithPendingVisits',
    transferredPatientsWithPendingVisits: 'pennTransferredPatientsWithPendingVisits',
    agencyDischargedPatientsWithPendingVisits: 'pennAgencyDischargedPatientsWithPendingVisits',
    disciplineDischargePatientsWithPendingVisits:
        'pennDisciplineDischargePatientsWithPendingVisits',
    pendingHhaSupervisory: 'pennPendingHhaSupervisory',
    pendingTherapyReevaluation: 'pennPendingTherapyReevaluation',
    pendingTherapySupervisory: 'pennPendingTherapySupervisory',
    pendingLpnSupervisory: 'pennPendingLpnSupervisory',
    activePatientsWithNoVisits: 'pennActivePatientsWithNoVisits',
    unresolvedEpisodes: 'pennUnresolvedEpisodes',
    overdueVisits: 'pennOverdueVisits',
    potentialReschedules: 'pennPotentialReschedules',
    conflictingDischargeAndRecertVisits: 'pennConflictingDischargeAndRecertVisits',
} as const;
